import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { StatusCalculatorService } from '../../services/status-calculator.service';
import { TitleFormatPipe } from '../../pipes/title-format.pipe';
import { DateFormatPipe } from '../../pipes/date-format.pipe';
import { DateEolService } from '../../../services/eol/eol.service';
import { CommentsService } from '../../../services/comments/comments.service';
import {
  UpgradeService,
  IUpgradeRequest,
  IUpgradeResponse,
} from '../../../services/upgrade/upgrade.service';
import {
  ITechnologyTableRow,
  ITechnologyInfo,
  ITechnologyStack,
} from '@shared/models';
import { IFullProject, ITechStackMatrix } from '@features/models';

@Component({
  selector: 'app-tabla',
  imports: [CommonModule, FormsModule, TitleFormatPipe, DateFormatPipe],
  templateUrl: './tabla.component.html',
  styleUrls: ['./tabla.component.scss'],
})
export class TablaComponent implements OnInit {
  @Input() ec2Repositories: IFullProject[] = [];
  repositoriesArray: IFullProject[] = [];
  filteredRepositories: IFullProject[] = [];
  searchTerm = '';
  serverlessFilter: 'all' | 'serverless' | 'non-serverless' = 'all';
  isLoading: boolean = true;

  hoveredRow: { projectIndex: number; techIndex: number } | null = null;
  tableData: ITechnologyTableRow[] = [];

  eolStack: ITechStackMatrix[] = [];

  // Modal properties
  isModalOpen: boolean = false;
  selectedProject: string = '';
  currentComment: string = '';
  projectComments: Map<string, string> = new Map();

  // Upgrade confirm modal
  isUpgradeConfirmOpen: boolean = false;
  pendingUpgrade: IUpgradeRequest | null = null;
  isUpgrading: boolean = false;

  // Upgrade result modal
  upgradeResult: IUpgradeResponse | null = null;
  upgradeError: string | null = null;
  isUpgradeResultOpen: boolean = false;

  // Toast notification
  toast: { message: string; type: 'success' | 'error' } | null = null;
  private toastTimer: any = null;

  constructor(
    readonly _statusCalculatorService: StatusCalculatorService,
    readonly _eolService: DateEolService,
    readonly _commentsService: CommentsService,
    readonly _upgradeService: UpgradeService,
    readonly router: Router,
  ) {}

  ngOnInit() {
    this.getEOLStack();
  }

  async getEOLStack(): Promise<void> {
    try {
      this.eolStack = await this._eolService.fetchEOLStack();
    } catch (error) {
      console.error(error);
    }
  }

  ngOnChanges() {
    if (Array.isArray(this.ec2Repositories)) {
      this.isLoading = false;
      this.repositoriesArray = this.ec2Repositories;
      this.filteredRepositories = [...this.ec2Repositories];
    }
  }

  /**
   * Filtra los repositorios basado en el término de búsqueda y el filtro serverless
   */
  applyFilters(): void {
    let result = [...this.repositoriesArray];

    // Apply search filter
    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase();
      result = result.filter((repo) => {
        const matchesProject = repo.project.toLowerCase().includes(searchLower);
        const matchesFrontTech =
          repo.frontend?.stack?.some((tech: ITechnologyInfo) =>
            tech.tech?.toLowerCase().includes(searchLower),
          ) || false;
        const matchesBackendTech =
          repo.backend?.stack?.some((tech: ITechnologyInfo) =>
            tech.tech?.toLowerCase().includes(searchLower),
          ) || false;
        return matchesProject || matchesFrontTech || matchesBackendTech;
      });
    }

    // Apply serverless filter
    if (this.serverlessFilter !== 'all') {
      result = result.filter((repo) => {
        const topics: string[] =
          repo.topics || repo.frontend?.topics || repo.backend?.topics || [];
        const isServerless = topics.includes('serverless');
        return this.serverlessFilter === 'serverless'
          ? isServerless
          : !isServerless;
      });
    }

    this.filteredRepositories = result;
  }

  onSearch(): void {
    this.applyFilters();
  }

  setServerlessFilter(value: 'all' | 'serverless' | 'non-serverless'): void {
    this.serverlessFilter = value;
    this.applyFilters();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.serverlessFilter = 'all';
    this.filteredRepositories = [...this.repositoriesArray];
  }

  onRowHover(
    projectIndex: number,
    techIndex: number,
    isHovered: boolean,
  ): void {
    this.hoveredRow = isHovered ? { projectIndex, techIndex } : null;
  }

  isProjectColumnHovered(projectIndex: number): boolean {
    return (
      this.hoveredRow !== null && this.hoveredRow.projectIndex === projectIndex
    );
  }

  navigateToProject(projectName: string): void {
    // Find the complete project in the data
    const projectData = this.filteredRepositories.find(
      (repo) => repo.project === projectName,
    );

    if (projectData) {
      // Navigate passing the complete object as state
      this.router.navigate(['/project', projectName], {
        state: { projectData: projectData },
      });
    } else {
      // Fallback if project is not found
      this.router.navigate(['/project', projectName]);
    }
  }

  getRowSpan(technologies: any[]): number {
    return technologies.length;
  }

  mapToITechStack(tech: ITechnologyInfo, techName: string): ITechnologyStack {
    return {
      tech: techName,
      version: tech.version,
      releaseDate: tech.releaseDate,
      latest: tech.latest ?? '',
      latestReleaseDate: tech.latestReleaseDate ?? '',
      lts: tech.lts ?? false,
      support: tech.support,
      eol: tech.eol ?? false,
      extendedSupport: tech.extendedSupport,
      supportedJavaVersions: tech.supportedJavaVersions,
    };
  }

  calculateTechStatus(
    tech: ITechnologyInfo,
    techName: string,
    yearsAdvanced = 0,
  ): string {
    if (!tech || !techName) {
      return 'status-gray';
    }

    const finalTechName = techName || tech.tech || 'unknown';
    const techStack = this.mapToITechStack(tech, finalTechName);

    try {
      const status = this._statusCalculatorService.calculateStatus(
        techStack,
        yearsAdvanced,
      );
      return this._statusCalculatorService.getStatusCssClass(status);
    } catch (error) {
      console.warn('Error calculating tech status:', error);
      return 'status-gray';
    }
  }

  /**
   * Devuelve un array unificado (frontend primero, luego backend) para alinear correctamente
   * los items entre columnas usando un único *ngFor.
   */
  getCombinedStack(row: any): ITechnologyInfo[] {
    if (!row) return [];

    const frontend = Array.isArray(row?.frontend?.stack)
      ? row.frontend.stack
      : [];
    const back = Array.isArray(row?.backend?.stack) ? row.backend.stack : [];

    // Ensure each item has a valid tech
    const combinedStack = [...frontend, ...back].map((item) => ({
      ...item,
      tech: item.tech || 'unknown',
    }));

    return combinedStack;
  }

  /**
   * Obtiene todas las tecnologías (frontend + backend) de un proyecto
   */
  getAllTechnologies(row: any): ITechnologyInfo[] {
    const frontend = Array.isArray(row?.frontend?.stack)
      ? row.frontend.stack
      : [];
    const backend = Array.isArray(row?.backend?.stack) ? row.backend.stack : [];
    return [...frontend, ...backend];
  }

  getTotalTechnologies(row: any): number {
    return this.getAllTechnologies(row).length;
  }

  formatEolDate(eol: string | boolean | undefined): string | null {
    if (eol && typeof eol === 'string' && eol !== '') {
      return eol;
    }
    return null;
  }

  getRecommendedVersion(techName: string, currentVersion?: string): string {
    return this._eolService._getRecommendedVersion(
      techName,
      this.eolStack,
      currentVersion,
    );
  }

  trackByIndex(index: number): number {
    return index;
  }

  openCommentModal(projectName: string): void {
    this.selectedProject = projectName;
    this.currentComment = this.projectComments.get(projectName) || '';
    this.isModalOpen = true;

    // Cargar el contenido después de que el modal se abra
    setTimeout(() => {
      const editor = document.querySelector('.rich-text-editor') as HTMLElement;
      if (editor) {
        editor.innerHTML = this.currentComment;
      }
    }, 0);
  }

  closeCommentModal(): void {
    this.isModalOpen = false;
    this.selectedProject = '';
  }

  viewComments(projectName: string): void {
    // Find the complete project in the data
    const projectData = this.filteredRepositories.find(
      (repo) => repo.project === projectName,
    );

    if (projectData) {
      // Navigate passing the complete object and scrollToComments flag as state
      this.router.navigate(['/project', projectName], {
        state: { projectData: projectData, scrollToComments: true },
      });
    } else {
      // Fallback if project is not found
      this.router.navigate(['/project', projectName], {
        state: { scrollToComments: true },
      });
    }
  }

  async saveComment(): Promise<void> {
    if (this.selectedProject && this.currentComment) {
      try {
        // Guardar en el mapa local
        this.projectComments.set(this.selectedProject, this.currentComment);

        // Enviar al servicio
        const response = await this._commentsService.createComment(
          this.selectedProject,
          this.currentComment,
        );

        this.closeCommentModal();
      } catch (error) {
        console.error('Error saving comment:', error);
        // Podrías mostrar un mensaje de error al usuario aquí
      }
    } else {
      this.closeCommentModal();
    }
  }

  onEditorInput(event: Event): void {
    const element = event.target as HTMLElement;
    this.currentComment = element.innerHTML;
  }

  formatText(command: string): void {
    document.execCommand(command, false, undefined);
  }

  getEolUrgencyClass(eol: string | boolean | undefined): string {
    const dateStr = this.formatEolDate(eol);
    if (!dateStr) return '';
    const eolDate = new Date(dateStr);
    const now = new Date();
    if (eolDate < now) return 'eol-expired';
    const sixMonths = new Date();
    sixMonths.setMonth(sixMonths.getMonth() + 6);
    if (eolDate <= sixMonths) return 'eol-warning';
    return '';
  }

  getCommentCount(row: IFullProject): number {
    return Array.isArray(row.comments) ? row.comments.length : 0;
  }

  getProjectCount(): number {
    return this.filteredRepositories.length;
  }

  getTotalProjectCount(): number {
    return this.repositoriesArray.length;
  }

  isUpToDate(recommendedVersion: string): boolean {
    return recommendedVersion === 'Up to date';
  }

  getNextVersion(techName: string, currentVersion?: string): string | null {
    return this._eolService._getNextVersion(
      techName,
      this.eolStack,
      currentVersion,
    );
  }

  openUpgradeConfirm(
    project: string,
    techName: string,
    version: string,
    targetVersion: string,
  ): void {
    this.pendingUpgrade = {
      project_name: project,
      technology: techName,
      current_version: version,
      target_version: targetVersion,
    };
    this.isUpgradeConfirmOpen = true;
  }

  cancelUpgrade(): void {
    this.isUpgradeConfirmOpen = false;
    this.pendingUpgrade = null;
  }

  async confirmUpgrade(): Promise<void> {
    if (!this.pendingUpgrade) return;
    this.isUpgrading = true;
    try {
      const response = await this._upgradeService.triggerUpgrade(
        this.pendingUpgrade,
      );
      this.upgradeResult = response;
      this.upgradeError = null;
      this.isUpgradeResultOpen = true;
    } catch {
      this.upgradeResult = null;
      this.upgradeError =
        'Failed to trigger upgrade workflow. Please try again.';
      this.isUpgradeResultOpen = true;
    } finally {
      this.isUpgrading = false;
      this.isUpgradeConfirmOpen = false;
      this.pendingUpgrade = null;
    }
  }

  closeUpgradeResult(): void {
    this.isUpgradeResultOpen = false;
    this.upgradeResult = null;
    this.upgradeError = null;
  }

  private showToast(message: string, type: 'success' | 'error'): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toast = { message, type };
    this.toastTimer = setTimeout(() => (this.toast = null), 5000);
  }
}
